import { PencilSquareIcon } from "@heroicons/react/24/outline";
import { GroupChats } from "@/components/groupChat/GroupChats";
import React, { useEffect } from "react";
import { Fragment, useState, useRef } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useForm } from "react-hook-form";
import useSWR, { useSWRConfig } from "swr";
import { fetchOrganization } from "@/repositories/organization";
import { useCurrentUser } from "@/hoc/useCurrentUser";
import { useToast } from "@/utils";
import { client } from "@/api/client";
import { newGroupChatRepository } from "@/repositories/groupChat";

export const Sidebar = () => {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const { currentUser } = useCurrentUser();
  const { mutate } = useSWRConfig();

  const { data } = useSWR(
    `/api/v1/organizations/${currentUser?.organization_id}`,
    (url) => fetchOrganization(url)
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  const groupChatRepository = newGroupChatRepository(client);
  const onFinish = async (data: any) => {
    try {
      await groupChatRepository.createGroupChat(data);
      toast.success("作成しました。");
      await mutate("/api/v1/group_chats");
      setOpen(false);
    } catch (e) {
      toast.error("エラーが発生しました。");
    }
  };

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  const cancelButtonRef = useRef(null);

  return (
    <aside className="w-full md:w-96 flex-shrink-0 md:border-r border-gray-200 xl:order-first xl:flex xl:flex-col">
      <div className="border-b border-gray-200 flex items-center justify-between px-2 py-5">
        <h3 className="text-lg font-medium leading-6 text-gray-900">
          チャット
        </h3>
        <div className="mt-3 sm:mt-0 sm:ml-4">
          <button
            onClick={() => setOpen(true)}
            type="button"
            className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-2 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          >
            <PencilSquareIcon className={"w-5 mr-2"} />
            グループを追加
          </button>
        </div>
      </div>
      <GroupChats />
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={setOpen}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                  <div className="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
                    <button
                      type="button"
                      className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      onClick={() => setOpen(false)}
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>
                  <form
                    onSubmit={handleSubmit((data) => onFinish(data))}
                    className={""}
                  >
                    <div className={"py-10 px-6"}>
                      <div className="border-b border-gray-200 pb-3 mb-5">
                        <h3 className="text-lg font-medium leading-6 text-gray-900">
                          グループを新規作成
                        </h3>
                      </div>
                      <div className={"mb-5"}>
                        <label
                          htmlFor="email"
                          className="block text-sm font-medium text-gray-700"
                        >
                          タイトル
                        </label>
                        <div className="mt-1">
                          <input
                            type="text"
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            {...register("title", {
                              required: true,
                            })}
                          />
                        </div>
                        {errors.title && (
                          <span className={"block text-red-500 mt-2 text-xs"}>
                            {"タイトルを入力してください。"}
                          </span>
                        )}
                      </div>
                      <p className="font-medium text-gray-900 text-sm">
                        メンバー
                      </p>
                      <div className="mt-2 divide-y divide-gray-200 border-t border-b border-gray-200 max-h-[250px] overflow-auto">
                        {data?.users
                          .filter((user) => user.id !== currentUser?.id)
                          .map((user, index) => (
                            <div
                              key={index}
                              className="relative flex items-center"
                            >
                              <div className="min-w-0 flex-1 text-sm">
                                <label
                                  htmlFor={`member-${index}`}
                                  className="select-none font-medium text-gray-700 cursor-pointer block py-4"
                                >
                                  {user.full_name}
                                </label>
                              </div>
                              <div className="ml-3 flex h-5 items-center p-4">
                                <input
                                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                                  type="checkbox"
                                  id={`member-${index}`}
                                  value={user.id}
                                  {...register("user_ids", { required: true })}
                                />
                              </div>
                            </div>
                          ))}
                      </div>
                      {errors.user_ids && (
                        <span className={"block text-red-500 mt-2 text-xs"}>
                          {"メンバーを選択ください。"}
                        </span>
                      )}
                    </div>
                    <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                      <button
                        type="submit"
                        className="inline-flex w-full justify-center rounded-md border border-transparent bg-blue-700 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
                      >
                        作成する
                      </button>
                      <button
                        type="button"
                        className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                        onClick={() => setOpen(false)}
                        ref={cancelButtonRef}
                      >
                        キャンセル
                      </button>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </aside>
  );
};
